/**
 * Composable for managing Open Graph meta tags
 * Provides a centralized way to set Open Graph metadata for link previews
 * 
 * ⚠️ IMPORTANT LIMITATION:
 * This sets meta tags client-side (via JavaScript). Social media crawlers 
 * (Slack, Facebook, Twitter, etc.) don't execute JavaScript, so they will 
 * see the default tags from index.html instead of the dynamic values.
 * 
 * For proper social media support, you need:
 * 1. Server-side rendering (SSR) - e.g., Nuxt.js
 * 2. A meta tag service like Prerender.io
 * 3. A backend endpoint that serves HTML with correct meta tags for crawlers
 * 
 * Current behavior: Tags are set after page load, which works for:
 * - Users who visit the page first (some platforms cache)
 * - Platforms that execute JavaScript (limited support)
 */

/**
 * Set or update an Open Graph meta tag
 * @param property - The Open Graph property (e.g., 'og:title', 'og:description')
 * @param content - The content value for the meta tag
 */
function setOpenGraphMeta(property: string, content: string): void {
  if (!content) return

  // Remove the 'og:' prefix if present for the attribute name
  const attributeName = property.startsWith('og:') ? property : `og:${property}`
  
  // Find existing meta tag or create new one
  let metaTag = document.querySelector(`meta[property="${attributeName}"]`) as HTMLMetaElement
  
  if (!metaTag) {
    metaTag = document.createElement('meta')
    metaTag.setAttribute('property', attributeName)
    document.head.appendChild(metaTag)
  }
  
  metaTag.setAttribute('content', content)
}

/**
 * Remove an Open Graph meta tag
 * @param property - The Open Graph property to remove
 */
function removeOpenGraphMeta(property: string): void {
  const attributeName = property.startsWith('og:') ? property : `og:${property}`
  const metaTag = document.querySelector(`meta[property="${attributeName}"]`)
  if (metaTag) {
    metaTag.remove()
  }
}

/**
 * Set Open Graph metadata for a test run
 * @param testRunId - The test run ID (e.g., 'TR00001')
 * @param testRunName - The test run name
 * @param description - Optional description (defaults to test run name)
 */
export function setTestRunOpenGraph(testRunId: string, testRunName: string, description?: string): void {
  if (!testRunId || !testRunName) return

  const title = `${testRunId} - ${testRunName}`
  const desc = description || testRunName
  const url = window.location.href

  setOpenGraphMeta('og:title', title)
  setOpenGraphMeta('og:description', desc)
  setOpenGraphMeta('og:url', url)
  setOpenGraphMeta('og:type', 'website')
}

/**
 * Reset Open Graph metadata to default values
 */
export function resetOpenGraphMeta(): void {
  setOpenGraphMeta('og:title', 'Kaizen Platform')
  setOpenGraphMeta('og:description', 'Kaizen Platform - Test Management System')
  setOpenGraphMeta('og:type', 'website')
  // Keep og:url as current URL
  setOpenGraphMeta('og:url', window.location.href)
}

/**
 * Composable to manage Open Graph meta tags with automatic cleanup
 */
export function useOpenGraph() {
  return {
    setTestRunOpenGraph,
    resetOpenGraphMeta,
    setMeta: setOpenGraphMeta,
    removeMeta: removeOpenGraphMeta
  }
}

